import { createStyles, Global, Stack } from '@mantine/core';
import type { ActivityLogOut } from '@repo/api-codegen';
import { apiQueryKey, fetchApiListResourceLogs } from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { EventBlock } from '@repo/common/components';
import { stripMarkdownLinks } from '@repo/common/utils';
import { Text } from '@repo/foundations';
import { useQueryNormalizer } from '@repo/react-query-cache';
import { useQueryClient, useSuspenseQueries } from '@tanstack/react-query';
import axios from 'axios';
import { observer } from 'mobx-react-lite';
import { memo, Suspense, useState } from 'react';
import type { IQuestion, IQuestionReply } from '../../api';
import {
	fetchQuestionReplyList,
	questionReplyQueryKeyFactory,
	questionsQueryKeyFactory,
	useAIFeedback,
	useAuthUser,
	useCreateQuestionReply,
	useDeleteQuestionReply,
	useUpdateQuestion,
	useUpdateQuestionReply,
} from '../../api';
import { getEndpoints } from '../../api/common';
import { getDefaultQueryFn } from '../../api/hooks/base/useBaseModel';
import { useQuestionPermissions } from '../../api/hooks/question/permissions';
import type { IActivityLogPerformer } from '../../components/ActivityLogPerformer/types';
import { useDebouncedCallbackWithFlush } from '../../hooks/useDebouncedCallbackWithFlush';
import { formatShortTimeDifference } from '../../utils/time';
import { AiChatEmbedPreviewController } from '../AiChatEmbedPreview';
import { SlackEmbedPreviewController } from '../SlackEmbedPreview';
import { ActivityLogBody } from './components/ActivityLogBody';
import { AiLegacyReply } from './components/AiLegacyReply';
import { AiReply } from './components/AiReply';
import { QuestionContentSkeleton } from './components/QuestionContentSkeleton';
import { QuestionEditor } from './components/QuestionEditor';
import { QuestionNewReply } from './components/QuestionNewReply';
import { QuestionTitle } from './components/QuestionTitle';
import { Reactions } from './components/Reactions';
import { UserReply } from './components/UserReply';
import { ViewportWrapper } from './components/ViewportWrapper';
import { QUESTION_REPLY_LOCAL_STORAGE_KEY } from './constants';
import { QuestionStore } from './store';
import { QuestionReplyType } from './types';
import { getTTLCacheItem, invalidateTTLCacheItem } from './utils';

interface QuestionControllerProps {
	id: string;
	includeArchived?: boolean;
	focusedReplyId?: string;
	deletedReplyInfo?: {
		owner?: {
			id: string;
			display_name: string;
			profile_picture?: string;
		} | null;
		deletedAt?: string | null;
	};
}

const useStyles = createStyles((theme) => ({
	text: {
		paddingInlineEnd: theme.spacing['3xs'],
	},
}));

const QuestionContentControllerInternal = observer(
	({
		id,
		includeArchived = false,
		focusedReplyId,
		deletedReplyInfo,
	}: QuestionControllerProps) => {
		const queryClient = useQueryClient();
		const { classes } = useStyles();
		const queryNormalizer = useQueryNormalizer();
		const { mutateAsync: updateQuestion } = useUpdateQuestion({
			options: {
				onSuccess: () => {
					// Invalidate question metrics query key
					queryClient.invalidateQueries({
						queryKey: apiQueryKey('question/metrics'),
					});
				},
			},
		});
		const { mutateAsync: createQuestionReply } = useCreateQuestionReply({
			options: {
				onSuccess: (data) => {
					// update question status when reply is created
					queryNormalizer.setNormalizedData(data.question);
					// Invalidate question metrics query key
					queryClient.invalidateQueries({
						queryKey: apiQueryKey('question/metrics'),
					});
				},
			},
		});
		const { mutateAsync: updateQuestionReply } = useUpdateQuestionReply({
			options: {
				onSuccess: (data) => {
					// update question status when reply is updated
					// current use case if when reply is marked as answer, we change question status to answered
					queryNormalizer.setNormalizedData(data.question);
					// Invalidate question metrics query key
					queryClient.invalidateQueries({
						queryKey: apiQueryKey('question/metrics'),
					});
				},
			},
		});
		const { mutateAsync: deleteQuestionReply } = useDeleteQuestionReply({});

		const suspenseData = useSuspenseQueries({
			queries: [
				{
					queryKey: questionsQueryKeyFactory.byId(id),
					queryFn: getDefaultQueryFn<IQuestion>(
						id,
						questionsQueryKeyFactory.namespace,
						includeArchived ? { include_archived: 'true' } : undefined
					),
				},
				{
					queryKey: questionReplyQueryKeyFactory.list(1, {
						question_id: id,
					}),
					queryFn: async ({ signal }) => {
						const response = await axios.get(
							`${getEndpoints(questionReplyQueryKeyFactory.namespace).root()}?question_id=${id}`,
							{ signal }
						);
						return response.data;
					},
				},
			],
		});

		const question = suspenseData[0]?.data;
		let replies: IQuestionReply[] = suspenseData[1]?.data.results ?? [];

		// Inject a synthetic deleted reply so it renders like a normal reply
		if (
			focusedReplyId &&
			deletedReplyInfo &&
			!(replies || []).some((r) => r.id === focusedReplyId)
		) {
			const deletedAt = deletedReplyInfo.deletedAt ?? new Date().toISOString();
			const owner = deletedReplyInfo.owner
				? {
						id: deletedReplyInfo.owner.id,
						display_name: deletedReplyInfo.owner.display_name,
						profile_picture: deletedReplyInfo.owner.profile_picture,
					}
				: undefined;

			const synthetic = {
				id: focusedReplyId,
				entity_type: 'question_reply',
				created_at: deletedAt,
				updated_at: deletedAt,
				definition: 'deleted',
				reactions: [],
				accepted_answer: false,
				current_user_vote: 0,
				status: '',
				priority: '',
				display_metadata: {
					owners: owner ? [owner] : [],
					owners_groups: [],
					collections: [],
				},
			} as unknown as IQuestionReply;

			replies = [...replies, synthetic];
		}

		const { user, workspace } = useAuthUser();
		const cacheKey = `${QUESTION_REPLY_LOCAL_STORAGE_KEY}-${id}`;
		const replyInputInitialValue = getTTLCacheItem(cacheKey) || '';

		const permissions = useQuestionPermissions(question, 'question');

		const [titleValue, setTitleValue] = useState(
			stripMarkdownLinks(question?.title ?? '')
		);

		const [store] = useState(
			() =>
				new QuestionStore({
					user,
					question,
					replies,
					replyInputInitialValue,
					permissions,
					focusedReplyId,
					updateQuestion: (data) => updateQuestion({ data: { id, ...data } }),
					fetchQuestionReplyList: async () =>
						await fetchQuestionReplyList({
							filters: {
								question_id: id,
							},
							options: {
								staleTime: 0,
							},
						}),
					fetchQuestionReply: async (replyId: string) =>
						await queryClient.fetchQuery<IQuestionReply>({
							queryKey: questionReplyQueryKeyFactory.byId(replyId),
							queryFn: async () =>
								(
									await axios.get(
										getEndpoints(questionReplyQueryKeyFactory.namespace).byId(
											replyId
										)
									)
								).data,
							staleTime: 0,
						}),
					createQuestionReply: async (data) => {
						const reply = await createQuestionReply({ data });
						trackEvent(
							'question/reply/create',
							{
								id,
							},
							user,
							workspace
						);
						invalidateTTLCacheItem(cacheKey);
						return reply;
					},
					updateQuestionReply: (data) => updateQuestionReply({ data }),
					deleteQuestionReply: (replyId) =>
						deleteQuestionReply({ id: replyId }),
					fetchActivityLogs: async () => {
						const response = await fetchApiListResourceLogs({
							queryParams: {
								resource_id: id,
								page: 1,
							},
						});
						const logs: ActivityLogOut[] = [];
						const idsSeen: Set<string> = new Set();

						// Occasionally on some older activity logs the same log can appear in both
						// the non-versioned logs and the version histories, so we do some basic filtering here
						for (const log of response.non_versioned_logs ?? []) {
							if (!idsSeen.has(log.id)) {
								idsSeen.add(log.id);
								logs.push(log);
							}
						}

						for (const versionHistory of response.version_histories ?? []) {
							for (const log of versionHistory.activity_logs ?? []) {
								if (!idsSeen.has(log.id)) {
									idsSeen.add(log.id);
									logs.push(log);
								}
							}
						}

						return logs;
					},
				})
		);

		/**
		 * Debounced function to persist title to store without causing effects in render cycle
		 * Backward-compatibility note:
		 * Older questions may have titles containing markdown link wrappers (e.g., [Name](/user/uuid)).
		 * New questions (from backend) have links stripped via markdown_links_strip.
		 * To support normalizing legacy titles and user edits to markdown formatting,
		 * we persist when visible text changes OR when only markdown link formatting changes.
		 */
		const debouncedUpdateTitle = useDebouncedCallbackWithFlush(
			(value: string) => {
				const current = store.question.title ?? '';
				const strippedNext = stripMarkdownLinks(value);
				const strippedCurrent = stripMarkdownLinks(current);
				const markdownLinkRegex = /\[[^\]]+\]\([^)]*\)/;

				const visibleChanged = strippedNext !== strippedCurrent;
				const markdownChangedButVisibleSame =
					value !== current &&
					(markdownLinkRegex.test(current) || markdownLinkRegex.test(value));

				if (visibleChanged || markdownChangedButVisibleSame) {
					store.update({ title: value });
				}
			},
			1000
		);

		const { showPositiveFeedbackModal, showNegativeFeedbackModal } =
			useAIFeedback();

		const owner = question?.display_metadata?.owners?.[0];

		return (
			<Stack>
				<Global
					styles={() => ({
						'.heading-actions, .block-menu-trigger': {
							display: 'none !important',
						},
					})}
				/>
				<ViewportWrapper>
					<Stack mt="3xl">
						<Stack spacing="md">
							<QuestionTitle
								readOnly={!permissions.canEdit}
								value={titleValue}
								originalTitle={stripMarkdownLinks(question?.title)}
								onChange={setTitleValue}
								onChangeDebounced={debouncedUpdateTitle}
							/>
							<QuestionEditor store={store} readOnly={!permissions.canEdit} />
						</Stack>
						{question.native_type === 'slack_thread' && question.url && (
							<SlackEmbedPreviewController questionId={question.id} />
						)}
						{question.ai_prompt && (
							<AiChatEmbedPreviewController promptId={question.ai_prompt} />
						)}
						<Reactions
							reactions={question.reactions}
							onToggle={store.toggleReaction}
							canReact={store.permissions.canReact}
						/>
					</Stack>
				</ViewportWrapper>
				<Stack spacing="md">
					<ViewportWrapper>
						<Text size="md" weight="bold">
							Activity
						</Text>
					</ViewportWrapper>
					{owner && 'display_name' in owner && 'profile_picture' in owner && (
						<ActivityLogBody
							performer={
								{
									id: owner.id,
									type: 'USER',
									metadata: owner,
								} as IActivityLogPerformer
							}
						>
							<Text
								size="sm"
								color="text/secondary/default"
								span
								className={classes.text}
							>
								created the question
							</Text>
							<Text
								size="sm"
								color="text/secondary/default"
								span
								className={classes.text}
							>
								·
							</Text>
							<Text
								size="sm"
								color="text/secondary/default"
								span
								className={classes.text}
							>
								{formatShortTimeDifference(question.created_at, false, false)}
							</Text>
						</ActivityLogBody>
					)}
					{store.activityLogsAndReplies.map((item) => {
						if (item.type === 'activity_log') {
							if (
								item.data.activity_type === 'CREATE_ENTITY' &&
								(item.data.secoda_entity_type === 'question_reply' ||
									item.data.secoda_entity_type === 'question')
							) {
								// we don't want to show "XXX created question reply" - we just show the reply instead
								return null;
							}

							if (item.data.performer_metadata) {
								const performer = {
									id: item.data.performer_id ?? 'unknown',
									type: item.data.performer_type,
									metadata: item.data.performer_metadata,
								} as IActivityLogPerformer;

								return (
									<ActivityLogBody key={item.data.id} performer={performer}>
										<Text size="sm" span className={classes.text}>
											{item.data.event_blocks.map((block, index) => (
												<EventBlock
													// eslint-disable-next-line react/no-array-index-key
													key={index}
													block={block}
													last={index === item.data.event_blocks.length - 1}
												/>
											))}
										</Text>
										<Text
											size="sm"
											color="text/secondary/default"
											span
											className={classes.text}
										>
											·
										</Text>
										<Text
											size="sm"
											color="text/secondary/default"
											span
											className={classes.text}
										>
											{formatShortTimeDifference(
												item.data.logged_at,
												false,
												false
											)}
										</Text>
									</ActivityLogBody>
								);
							} else {
								return null;
							}
						}

						const reply = store.replies.get(item.reply_id);
						if (!reply) {
							return null;
						}

						switch (reply.replyType) {
							case QuestionReplyType.LegacyAi:
								return (
									<AiLegacyReply
										key={reply.reply.id}
										replyId={reply.reply.id}
										store={store}
										onClickPositiveFeedback={
											reply.reply.embedded_prompt
												? () =>
														showPositiveFeedbackModal(
															reply.reply.embedded_prompt!.id,
															reply.updateUserFeedback
														)
												: undefined
										}
										onClickNegativeFeedback={
											reply.reply.embedded_prompt
												? () =>
														showNegativeFeedbackModal(
															reply.reply.embedded_prompt!.id,
															reply.updateUserFeedback
														)
												: undefined
										}
									/>
								);
							case QuestionReplyType.Ai:
								return (
									<AiReply
										key={reply.reply.id}
										replyId={reply.reply.id}
										store={store}
										onClickPositiveFeedback={
											reply.reply.embedded_prompt
												? () =>
														showPositiveFeedbackModal(
															reply.reply.embedded_prompt!.id,
															reply.updateUserFeedback
														)
												: undefined
										}
										onClickNegativeFeedback={
											reply.reply.embedded_prompt
												? () =>
														showNegativeFeedbackModal(
															reply.reply.embedded_prompt!.id,
															reply.updateUserFeedback
														)
												: undefined
										}
									/>
								);
							default:
							case QuestionReplyType.User:
								return (
									<UserReply
										key={reply.reply.id}
										replyId={reply.reply.id}
										store={store}
									/>
								);
						}
					})}
					{permissions.canReply && (
						<ViewportWrapper>
							<QuestionNewReply store={store} />
						</ViewportWrapper>
					)}
				</Stack>
			</Stack>
		);
	}
);

export const QuestionContentController = memo(
	({
		id,
		includeArchived,
		focusedReplyId,
		deletedReplyInfo,
	}: QuestionControllerProps) => (
		<Suspense fallback={<QuestionContentSkeleton />}>
			<QuestionContentControllerInternal
				id={id}
				includeArchived={includeArchived}
				focusedReplyId={focusedReplyId}
				deletedReplyInfo={deletedReplyInfo}
			/>
		</Suspense>
	)
);
